import { useCallbackOne as useCallback } from 'use-memo-one';
import gql from 'graphql-tag';
import { commitMutation, graphql, useMutation } from 'react-relay';
import { type ContextId, type ExtensionId } from '@atlassian/forge-ui-types';
import {
	type GQLInvokeExtensionEntrypoint,
	type GQLInvokeExtensionInput,
	type GQLInvokeExtensionPayload,
	type GQLInvokeExtensionResponse,
} from '../graphql/types';
import { createAtlAttributionHeader } from '../../utils';
import type { DocumentNode } from 'graphql';
import { type useInvokeExtensionRelayMutation } from './__generated__/useInvokeExtensionRelayMutation.graphql';

export interface MutationVariables {
	input: GQLInvokeExtensionInput;
}

export interface MutationData {
	invokeExtension: GQLInvokeExtensionResponse;
}

export type InvokeExtensionInvoker = (
	payload: GQLInvokeExtensionPayload,
	entryPoint?: GQLInvokeExtensionEntrypoint,
) => Promise<{ data?: MutationData }>;

export const invokeExtensionMutation: DocumentNode = gql`
	mutation forge_ui_invokeExtension($input: InvokeExtensionInput!) {
		invokeExtension(input: $input) {
			success
			response {
				body
			}
			contextToken {
				jwt
				expiresAt
			}
			rateLimitProperties {
				rateLimitRemaining
				rateLimitReset
				rateLimitValue
			}
			errors {
				message
				extensions {
					errorType
					statusCode
					... on InvokeExtensionPayloadErrorExtension {
						fields {
							authInfoUrl
						}
					}
				}
			}
		}
	}
`;

export const useInvokeExtension: (...args: any[]) => any = (
	contextIds: ContextId[],
	extensionId: ExtensionId,
) => {
	const customCommitFn = useCallback(
		(environment: Parameters<typeof commitMutation>[0], config: any) =>
			commitMutation<useInvokeExtensionRelayMutation>(environment, {
				...config,
				cacheConfig: {
					metadata: {
						headers: {
							'atl-attribution': createAtlAttributionHeader(contextIds.sort().join()),
						},
					},
				},
			}),
		[contextIds],
	);

	const [commit] = useMutation<useInvokeExtensionRelayMutation>(
		graphql`
			mutation useInvokeExtensionRelayMutation($input: InvokeExtensionInput!) {
				invokeExtension(input: $input) {
					success
					response {
						body
					}
					contextToken {
						jwt
						expiresAt
					}
					rateLimitProperties {
						rateLimitRemaining
						rateLimitReset
						rateLimitValue
					}
					errors {
						message
						extensions {
							errorType
							statusCode
							... on InvokeExtensionPayloadErrorExtension {
								fields {
									authInfoUrl
								}
							}
						}
					}
				}
			}
		`,
		customCommitFn,
	);

	return useCallback(
		(payload: GQLInvokeExtensionPayload, entryPoint: GQLInvokeExtensionEntrypoint = 'resolver') =>
			new Promise<{ data?: MutationData }>((resolve, reject) => {
				commit({
					variables: {
						input: {
							contextIds,
							extensionId,
							payload,
							entryPoint,
						},
					},
					onCompleted: (data: unknown, errors: unknown) => {
						if (Array.isArray(errors) && errors.length > 0) {
							// We pass the error to `resolve` here so it can goes through the error handling logic found in packages/forge/forge-ui/src/custom-ui/iframe/InnerIframe.tsx - onInvoke
							resolve({
								data: {
									invokeExtension: {
										errors,
										success: false,
										response: null,
										contextToken: null,
									},
								} as unknown as MutationData,
							});
						}

						resolve({ data: data as MutationData });
					},
					onError: reject,
				});
			}),
		[contextIds, extensionId, commit],
	);
};
